<template>
  <v-container fluid>
    <h2>{{ $tt('observation.accumulatedTemperatureList') }}</h2>

    <v-btn class="ma-3" small color="warning" @click="openEditor()" v-if="canCreateNew()">
      <v-icon>mdi-plus</v-icon>
      {{ newButtonDescription }}
    </v-btn>

    <v-list three-line-line v-for="oInfo in atObservationInfos">
      <v-list-item>
        <v-list-item-avatar tile width="100" height="100">
          <v-img :src="thumbnailUrl(oInfo.field)" />
        </v-list-item-avatar>
        <v-list-item-content class="col-3 pa-0 ma-0">
          <v-list-item-title>
            {{ oInfo.field.name }}
          </v-list-item-title>
          <v-list-item-subtitle>
            <div v-for="planting in listCurrentPlantings(oInfo.field)">
              <planting-indicator-component :planting="planting" small :display-year="false" />
            </div>
          </v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-content>
          <v-list-item-content v-if="!oInfo.field.deleted">
            <v-list-item-title>
              <v-progress-circular indeterminate v-if="oInfo.weatherModelFromOpenMeteo.roundedTemperature === '??'" />
              <h2 v-else>{{ oInfo.weatherModelFromOpenMeteo.roundedTemperature | aidTemperature }}</h2>
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ $tt('observation.startDateCalculation') }}: {{ oInfo.observation.startDate }}
            </v-list-item-subtitle>
            <v-list-item-subtitle v-if="oInfo.observation.endDate">
              {{ $tt('observation.endDateCalculation') }}: {{ oInfo.observation.endDate }}
            </v-list-item-subtitle>
            <v-list-item-subtitle>
              {{ $tt('observation.lowerLimitTemperature') }}:
              {{ oInfo.observation.lowerLimitTemperature | aidTemperature }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item-content>
        <v-list-item-action>
          <div>
            <v-btn icon @click="openEditor(oInfo)" v-if="!oInfo.field.deleted">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon @click="openDeleteDialog(oInfo)">
              <v-icon>mdi-trash-can-outline</v-icon>
            </v-btn>
          </div>
        </v-list-item-action>
      </v-list-item>
      <v-col class="col-12 mt-4" v-if="oInfo.weatherModelFromOpenMeteo && isOpenMeteoEnabled">
        <chart-component
          :observed-data="oInfo.weatherModelFromOpenMeteo.observedList"
          v-if="oInfo.weatherModelFromOpenMeteo.observedList"
        />
      </v-col>
    </v-list>

    <!--
    編集ダイアログ
    -->
    <v-dialog v-model="showingEditor" max-width="80%" persistent>
      <v-card>
        <v-card-title>
          {{ $tt('observation.registerAndEditAccumulatedTemperaturePoints') }}
        </v-card-title>
        <v-card-text>
          <v-card outlined>
            <my-field-select-component
              :multiple="false"
              v-model="selectedFieldPk"
              :error="editorErrors.fieldInput.error"
              :error-message="editorErrors.fieldInput.message"
              field-value="id"
            />
          </v-card>
          <v-card outlined class="mt-3">
            <v-card-title>
              <h6>{{ $tt('observation.period') }}</h6>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col class="py-0">
                  <v-menu
                    ref="menuStartDate"
                    v-model="showingStartDateMenu"
                    :close-on-content-click="false"
                    :return-value.sync="startDate"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="startDate"
                        :label="$tt('customPlantingSeasonStartDate')"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        :error="editorErrors.startDateInput.error"
                        :error-messages="editorErrors.startDateInput.message"
                      />
                    </template>
                    <v-date-picker
                      v-model="startDate"
                      no-title
                      scrollable
                      :day-format="date => new Date(date).getDate()"
                      @input="$refs.menuStartDate.save(startDate)"
                    />
                  </v-menu>
                </v-col>
                <v-col class="py-0">
                  <v-menu
                    ref="menuEndDate"
                    v-model="showingEndDateMenu"
                    :close-on-content-click="false"
                    :return-value.sync="endDate"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="endDate"
                        :label="$tt('customPlantingSeasonEndDate')"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        clearable
                        :error="editorErrors.endDateInput.error"
                        :error-messages="editorErrors.endDateInput.message"
                      />
                    </template>
                    <v-date-picker
                      v-model="endDate"
                      no-title
                      scrollable
                      :day-format="date => new Date(date).getDate()"
                      @input="$refs.menuEndDate.save(endDate)"
                    />
                  </v-menu>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
          <v-card outlined color="mt-3">
            <v-card-title>
              <h6>{{ $tt('observation.accumulatedLowerLimitTemperature') }}</h6>
            </v-card-title>
            <v-card-text>
              <v-text-field
                v-model:number="lowerLimitTemperature"
                type="number"
                prepend-icon="mdi-thermometer"
                :suffix="temperatureUnit"
              />
            </v-card-text>
          </v-card>
        </v-card-text>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn color="secondary" @click="closeEditor()">
            {{ $tt('customCancel') }}
          </v-btn>
          <v-btn color="primary" @click.stop="createOrUpdate()">
            {{ $tt('customSaveAction') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!--
    削除ダイアログ
    -->
    <v-dialog v-model="showingDeleteDialog" max-width="550px" persistent>
      <v-card>
        <v-alert prominent class="mb-0 pa-6">
          <div class="d-flex">
            <v-icon left large>warning</v-icon>
            <div>
              {{ deleteConfirmationMessage }}
            </div>
          </div>
        </v-alert>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn color="secondary" @click="closeDeleteDialog">
            {{ $tt('customCancel') }}
          </v-btn>
          <v-btn color="primary" @click="destroy()">
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import ObservationListMixin from '@/components/mixins/observation-list-mixin'
import MyFieldSelectComponent from '@/components/MyFieldSelectComponent'
import PlantingIndicatorComponent from '@/components/planting/PlantingIndicatorComponent'
import * as Constants from '@/lib/constants'
import i18n from '@/lib/i18n'
import * as ObservationConstants from '@/lib/observation-constants'
import ObservationInfo from '@/lib/observation-info'
import * as SystemOfUnits from '@/lib/system-of-units'
import UserInfo from '@/lib/user-info'
import moment from 'moment'
import ChartComponent from '@/components/observations/AccumulatedTemperatureChart.vue'
import ApplicationConfig from '@/lib/application-config'

const EDITOR_ERRORS = {
  fieldInput: { error: null, message: null },
  startDateInput: { error: null, message: null },
  endDateInput: { error: null, message: null },
}
export default {
  name: 'AccumulatedTemperatureListAsList',
  components: { MyFieldSelectComponent, PlantingIndicatorComponent, ChartComponent },
  mixins: [ObservationListMixin],
  data() {
    return {
      showingEditor: false,
      selectedObservationInfo: null,

      selectedFieldPk: null,
      startDate: null,
      endDate: null,
      lowerLimitTemperature: null,

      editorErrors: EDITOR_ERRORS,

      showingStartDateMenu: false,
      showingEndDateMenu: false,

      showingDeleteDialog: false,
      deleteConfirmationMessage: null,

      isOpenMeteoEnabled: ApplicationConfig.OPEN_METEO_ENABLED,
    }
  },
  computed: {
    atObservationInfos() {
      const observations = this.$store.getters.observations.at
      let list = []
      for (const observation of observations) {
        const oInfo = new ObservationInfo(observation, ObservationConstants.TYPE_ACCUMULATED_TEMPERATURE)
        list.push(oInfo)
      }
      return list
    },
    temperatureUnit() {
      return SystemOfUnits.getTemperatureUnit()
    },
    maxObservationCount() {
      const f = UserInfo.getFunctionality()
      return Constants.MaxObservationCount.AT[f]
    },
    newButtonDescription() {
      const count = this.maxObservationCount
      return i18n.tt('customNewRegistration') + i18n.tt('observation.maxRegisterCount', { count })
    },
  },
  mounted() {
    this.$store.dispatch('listAtObservations')
    this.initTemperature()
    this.initSelected()
    this.initErrors()
  },
  methods: {
    canCreateNew() {
      const totalCount = this.$store.getters.observations.at.length
      return totalCount < this.maxObservationCount
    },
    initTemperature() {
      if (SystemOfUnits.isMetric()) {
        this.lowerLimitTemperature = Constants.DEFAULT_LOWER_LIMIT_TEMPERATURE_FOR_ACCUMULATED
      } else {
        this.lowerLimitTemperature = SystemOfUnits.getFahrenheitValueFromCelsius(
          Constants.DEFAULT_LOWER_LIMIT_TEMPERATURE_FOR_ACCUMULATED
        )
      }
    },
    initSelected() {
      this.selectedObservationInfo = null
      this.selectedFieldPk = null
      this.startDate = null
      this.endDate = null
      this.lowerLimitTemperature = Constants.DEFAULT_LOWER_LIMIT_TEMPERATURE_FOR_ACCUMULATED
    },
    initErrors() {
      this.editorErrors = EDITOR_ERRORS
    },
    openEditor(oInfo) {
      this.initErrors()
      this.selectedObservationInfo = oInfo
      if (oInfo) {
        const observation = oInfo.observation
        if (UserInfo.isFSMigrated()) {
          this.selectedFieldPk = observation.fireFieldId
        } else {
          this.selectedFieldPk = observation.placeId
        }

        this.startDate = observation.startDate
        this.endDate = observation.endDate
        this.lowerLimitTemperature = observation.lowerLimitTemperature

        // 積算下限温度の単位による値調整
        if (SystemOfUnits.isImperial()) {
          this.lowerLimitTemperature = SystemOfUnits.getFahrenheitValueFromCelsius(observation.lowerLimitTemperature)
        }
      } else {
        this.initSelected()
      }
      this.showingEditor = true
    },
    closeEditor() {
      this.showingEditor = false
      this.initSelected()
      this.initErrors()
    },
    onSaveSuccess() {
      this.closeEditor()
    },
    onSaveFailure() {
      this.closeEditor()
    },
    createOrUpdate() {
      // エラー初期化
      this.initErrors()

      // バリデーション開始
      // 圃場が選択されているか
      if (!this.selectedFieldPk) {
        this.editorErrors.fieldInput.error = true
        this.editorErrors.fieldInput.message = i18n.tt('customErrorFieldRequired')
        return
      }

      // 開始日が設定されているか
      if (!this.startDate) {
        this.editorErrors.startDateInput.error = true
        this.editorErrors.startDateInput.message = i18n.tt('customErrorStartDateRequired')
        return
      }

      // 開始日が今日よりも前か
      const startMoment = moment(this.startDate)
      const now = moment()
      const yesterday = now.subtract(1, 'day')
      if (startMoment.isAfter(yesterday)) {
        this.editorErrors.startDateInput.error = true
        this.editorErrors.startDateInput.message = i18n.tt('observation.validateForStartDateBeforeYesterday')
        return
      }

      // 日付範囲のValidation
      const maxDays = Constants.MAX_DAYS_COUNT_FOR_ACCUMULATED_TEMPERATURE
      if (this.endDate) {
        // endDateが設定済みの場合
        // endがstartよりも後にあること
        const endMoment = moment(this.endDate)
        if (startMoment.isAfter(endMoment)) {
          this.editorErrors.endDateInput.error = true
          this.editorErrors.endDateInput.message = i18n.tt('observation.validateForStartEndDateOrder')
          return
        }
        // startとendの差がmaxDays未満であること
        const diff = endMoment.diff(startMoment, 'days')
// TEMP         console.log('diff', diff)
        if (diff > maxDays) {
          this.editorErrors.startDateInput.error = true
          this.editorErrors.endDateInput.error = true
          this.editorErrors.startDateInput.message = i18n.tt('observation.validateForStartEndDuration', {
            day: maxDays,
          })
          return
        }
      } else {
        // endDateが未設定の場合
        // startDateは現在から見てmaxDays未満であること
        const diff = now.diff(startMoment, 'days')
        if (diff > maxDays) {
          this.editorErrors.startDateInput.error = true
          this.editorErrors.endDateInput.error = true
          this.editorErrors.startDateInput.message = i18n.tt('observation.validateForStartNowDuration', {
            day: maxDays,
          })
          return
        }
      }

      // 積算下限温度が未設定の場合は強制デフォルト値
      if (!this.lowerLimitTemperature) {
        this.initTemperature()
      }

      let temp = this.lowerLimitTemperature
      if (SystemOfUnits.isImperial()) {
        temp = SystemOfUnits.getCelsiusValueFromFahrenheit(temp)
      }

      let request = {
        observation: {
          placeId: this.selectedFieldPk,
          placeType: 'Field',
          lowerLimitTemperature: temp,
          startDate: this.startDate,
          endDate: this.endDate,
          zoneId: moment.tz.guess(),
        },
      }

      if (UserInfo.isFSMigrated()) {
        request.observation.fireFieldId = this.selectedFieldPk
      }

      let action = null
      if (this.selectedObservationInfo) {
        request.id = this.selectedObservationInfo.observation.id
        action = 'updateAtObservation'
      } else {
        action = 'createAtObservation'
      }

      const params = { onSuccess: this.onSaveSuccess, onFailure: this.onSaveFailure, request: request }
      this.$store.dispatch(action, params)
    },

    openDeleteDialog(oInfo) {
      this.selectedObservationInfo = oInfo
      const fieldName = oInfo.field.name
      this.deleteConfirmationMessage = i18n.tt('observation.deleteAccumulatedTemperatureLocation', {
        fieldName: fieldName,
      })
      this.showingDeleteDialog = true
    },
    closeDeleteDialog() {
      this.initSelected()
      this.deleteConfirmationMessage = null
      this.showingDeleteDialog = false
    },

    onDeleteSuccess() {
      this.closeDeleteDialog()
    },
    onDeleteFailure() {
      this.closeDeleteDialog()
    },
    destroy() {
      const params = {
        request: { id: this.selectedObservationInfo.observation.id },
        onSuccess: this.onDeleteSuccess,
        onFailure: this.onDeleteFailure,
      }
      this.$store.dispatch('deleteAtObservation', params)
    },
  },
}
</script>
